import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress
} from '@mui/material'
import {
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Delete as RevokeIcon,
  Share as ShareIcon,
  Schedule as ScheduleIcon,
  Link as LinkIcon
} from '@mui/icons-material'
import toast from 'react-hot-toast'
import apiClient from '../../services/api'
import { Study } from '../../types/api'

interface ShareLinkDialogProps {
  open: boolean
  onClose: () => void
  study: Study | null
}

interface ShareLink {
  id: string
  token_type: string
  status: string
  created_at: string
  expires_at: string | null
  days_remaining: number | null
  use_count: number
  max_uses: number | null
  share_url: string | null
  revoke_reason: string | null
}

const ShareLinkDialog: React.FC<ShareLinkDialogProps> = ({ open, onClose, study }) => {
  const [ttlDays, setTtlDays] = useState(365)
  const [maxUses, setMaxUses] = useState<number | null>(null)
  const [generating, setGenerating] = useState(false)
  const [loading, setLoading] = useState(false)
  const [links, setLinks] = useState<ShareLink[]>([])
  const [generatedLink, setGeneratedLink] = useState<string | null>(null)

  useEffect(() => {
    if (open && study) {
      loadShareLinks()
    }
  }, [open, study])

  const loadShareLinks = async () => {
    if (!study?.study_uid) return

    try {
      setLoading(true)
      const response = await apiClient.get(`/studies/${study.study_uid}/share-links`)
      setLinks(response.data.links || [])
    } catch (err) {
      console.error('Eroare la încărcarea link-urilor:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateLink = async () => {
    if (!study?.study_uid) return

    try {
      setGenerating(true)
      const response = await apiClient.post(
        `/studies/${study.study_uid}/share-link`,
        null,
        {
          params: {
            ttl_days: ttlDays,
            max_uses: maxUses
          }
        }
      )

      const fullUrl = `${window.location.origin}${response.data.share_url}`
      setGeneratedLink(fullUrl)
      
      // Copy to clipboard
      await copyToClipboard(fullUrl)
      toast.success('Link generat și copiat în clipboard!')
      
      // Reload links
      loadShareLinks()
    } catch (err: any) {
      console.error('Eroare la generarea link-ului:', err)
      toast.error(err.response?.data?.detail || 'Eroare la generarea link-ului')
    } finally {
      setGenerating(false)
    }
  }

  const handleCopyLink = async (url: string) => {
    const fullUrl = `${window.location.origin}${url}`
    await copyToClipboard(fullUrl)
    toast.success('Link copiat în clipboard!')
  }

  const handleRevokeLink = async (linkId: string) => {
    if (!confirm('Sigur doriți să revocați acest link? Acțiunea este ireversibilă.')) {
      return
    }

    const reason = prompt('Motivul revocării:')
    if (!reason) return

    try {
      await apiClient.post(`/studies/share-links/${linkId}/revoke`, {
        revoke_reason: reason
      })
      toast.success('Link revocat cu succes')
      loadShareLinks()
    } catch (err: any) {
      console.error('Eroare la revocarea link-ului:', err)
      toast.error(err.response?.data?.detail || 'Eroare la revocarea link-ului')
    }
  }

  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text)
        return true
      } else {
        // Fallback pentru browsere mai vechi
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        try {
          document.execCommand('copy')
          document.body.removeChild(textArea)
          return true
        } catch (err) {
          document.body.removeChild(textArea)
          return false
        }
      }
    } catch (err) {
      console.error('Eroare la copierea în clipboard:', err)
      return false
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Permanent'
    return new Date(dateString).toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success'
      case 'expired': return 'error'
      case 'revoked': return 'warning'
      case 'max_uses_reached': return 'default'
      default: return 'default'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Activ'
      case 'expired': return 'Expirat'
      case 'revoked': return 'Revocat'
      case 'max_uses_reached': return 'Utilizări maxime atinse'
      default: return status
    }
  }

  return (
    <Dialog open={open} onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }} disableEscapeKeyDown maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ShareIcon />
            <Typography variant="h6">
              Sharing Link - {study?.patient_name}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Generare link nou */}
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Generare Link Nou
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Link-ul va permite pacientului să vizualizeze studiul DICOM prin browser, 
            fără autentificare. Valabil până la 2 ani.
          </Alert>

          <Box display="flex" gap={2} mb={2}>
            <FormControl fullWidth>
              <InputLabel>Valabilitate</InputLabel>
              <Select
                value={ttlDays}
                onChange={(e) => setTtlDays(Number(e.target.value))}
                label="Valabilitate"
              >
                <MenuItem value={7}>7 zile</MenuItem>
                <MenuItem value={30}>30 zile (1 lună)</MenuItem>
                <MenuItem value={90}>90 zile (3 luni)</MenuItem>
                <MenuItem value={180}>180 zile (6 luni)</MenuItem>
                <MenuItem value={365}>365 zile (1 an) - Recomandat</MenuItem>
                <MenuItem value={730}>730 zile (2 ani)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Utilizări maxime</InputLabel>
              <Select
                value={maxUses ?? 'unlimited'}
                onChange={(e) => setMaxUses(e.target.value === 'unlimited' ? null : Number(e.target.value))}
                label="Utilizări maxime"
              >
                <MenuItem value="unlimited">Nelimitat</MenuItem>
                <MenuItem value={1}>1 utilizare</MenuItem>
                <MenuItem value={5}>5 utilizări</MenuItem>
                <MenuItem value={10}>10 utilizări</MenuItem>
                <MenuItem value={50}>50 utilizări</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Button
            variant="contained"
            startIcon={generating ? <CircularProgress size={20} /> : <LinkIcon />}
            onClick={handleGenerateLink}
            disabled={generating || !study?.study_uid}
            fullWidth
          >
            {generating ? 'Se generează...' : 'Generează Link'}
          </Button>

          {generatedLink && (
            <Alert 
              severity="success" 
              sx={{ mt: 2 }}
              action={
                <IconButton size="small" onClick={() => handleCopyLink(generatedLink)}>
                  <CopyIcon />
                </IconButton>
              }
            >
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <strong>Link generat:</strong><br />
                {generatedLink}
              </Typography>
            </Alert>
          )}
        </Box>

        <Divider />

        {/* Link-uri active */}
        <Box mt={3}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Link-uri Active ({links.filter(l => l.status === 'active').length})
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : links.length === 0 ? (
            <Alert severity="info">Nu există link-uri generate pentru acest studiu</Alert>
          ) : (
            <List>
              {links.map((link, index) => (
                <React.Fragment key={link.id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip 
                            label={getStatusLabel(link.status)} 
                            size="small" 
                            color={getStatusColor(link.status) as any}
                          />
                          <Chip 
                            label={link.token_type} 
                            size="small" 
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box mt={1}>
                          <Typography variant="body2" color="text.secondary">
                            <ScheduleIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                            Creat: {formatDate(link.created_at)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <ScheduleIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                            Expiră: {formatDate(link.expires_at)}
                            {link.days_remaining !== null && link.status === 'active' && (
                              <Chip 
                                label={`${link.days_remaining} zile`} 
                                size="small" 
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Utilizări: {link.use_count} {link.max_uses ? `/ ${link.max_uses}` : '(nelimitat)'}
                          </Typography>
                          {link.revoke_reason && (
                            <Typography variant="body2" color="error">
                              Motiv revocare: {link.revoke_reason}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {link.status === 'active' && link.share_url && (
                        <Box display="flex" gap={1}>
                          <Tooltip title="Copiază link">
                            <IconButton 
                              edge="end" 
                              size="small"
                              onClick={() => handleCopyLink(link.share_url!)}
                            >
                              <CopyIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Revocă link">
                            <IconButton 
                              edge="end" 
                              size="small"
                              color="error"
                              onClick={() => handleRevokeLink(link.id)}
                            >
                              <RevokeIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ShareLinkDialog

